// Armătura, desenată în scena 3D.
//
// Datele vin din rezumatul serverului (`armare_3d.py`): câte bare, ce
// diametru, la ce pas și unde se înnădesc. Secțiunile și pozițiile vin din
// modelul Pasului 7, pe care scena îl are deja. Aici se face doar geometria.
//
// TOTUL E PLIN. Betonul layerului «Realist» e translucid tocmai ca armătura
// să fie singurul lucru solid din scenă; o carcasă desenată cu linii de un
// pixel nu e solidă — la depărtare se stinge, iar etrierul dispare exact
// acolo unde e cel mai des. Deci fiecare bară, fiecare etrier și fiecare
// bară de plasă e un CILINDRU, iar toate la un loc sunt un singur
// `InstancedMesh`: o geometrie, N matrice, UN desen pe cadru pentru armătura
// întregii clădiri. Ce le deosebește e culoarea per instanță
// (`InstancedMesh.instanceColor`), care nu costă niciun desen în plus.
//
// PATRU REGULI care nu sunt de stil, ci de citire.
//
// PASUL DESENAT NU E PASUL ARMAT. Un etrier la 10 cm, pe o clădire întreagă,
// se strânge într-o pată: la scara aia zece etrieri ocupă doi pixeli. Se
// desenează unul din k, cu k ales din pasul real, exact cum plasa unui ochi
// de placă se rărește în `Ochi3D`. Cifra rămâne cea din fișa elementului;
// desenul spune UNDE e armătură, nu CÂT.
//
// O BARĂ ARE O GROSIME MINIMĂ. Un Ø10 la scara unei clădiri de 20 m e sub
// jumătate de pixel: desenat la raza lui adevărată, ar dispărea. Raza se
// exagerează controlat (`EXAGERARE`, cu un prag jos), ca armătura să se
// subțieze cu depărtarea fără să se stingă.
//
// ÎNNĂDIREA SE VEDE CA ÎNNĂDIRE. Pe lungimea suprapunerii sunt DOUĂ bare una
// lângă alta, nu una: bara care vine de dedesubt e desenată deplasată spre
// interiorul carcasei, cu cârligul ei de trecere, și într-o culoare proprie.
// Așa se citește dintr-o privire unde se încheie o bară și începe alta —
// întrebarea pe care o pune orice om care se uită la o carcasă.
//
// NODUL E O PIESĂ, nu un loc unde se opresc două elemente. Barele stâlpului
// trec prin el și urcă în petrecerea nivelului de deasupra; la ultimul nivel
// se îndoaie în nod, fiindcă n-au unde continua. Barele grinzii intră în
// stâlp, iar la un nod de margine se întorc în el — ancorarea nu e o
// convenție de desen, e ce ține bara.
//
// COSTUL PE ECRAN. Totul fiind instanțe ale aceluiași cilindru, prețul e
// numărul lor, nu numărul de obiecte. De aceea există un BUGET: peste el,
// pasul desenat al etrierilor și al plaselor se rărește până se încadrează.
// O clădire de zece ori mai mare nu costă de zece ori mai mult — arată la
// fel, doar mai rar.

import * as THREE from "three";
import {
  FemFundatie, FemGrinda, FemGrindaFundare, FemPanou, FemResult, FemStalp,
} from "./tipuri";
import { REALIST } from "./realist3d";
import {
  Amprenta, amprentaPerete, amprentaStalp, arie, dreptunghiSegment, inPoligon,
} from "./amprente3d";
import { axaGrinzii } from "./axaGrinzii";

/** Familiile de COMPONENTE (`componente.py`): tipuri de bară și de etrier,
 *  clase de stâlp, de grindă și de grindă de fundare. */
export type FamilieComponenta = "bare" | "etrieri" | "stalpi" | "grinzi"
  | "fundatie";

/** Un tip, cât are nevoie meniul de evidențiere: id, descriere, utilizări. */
export interface TipComponenta {
  tip: string;
  descriere: string | null;
  nr: number | null;
  elemente?: number;
}

/** Ce se EVIDENȚIAZĂ în scenă: o familie întreagă (`tip` null) sau un singur
 *  tip din ea — «toți etrierii de tipul 3». */
export type Evidentiere = { cat: FamilieComponenta; tip: string | null } | null;

/** Rezumatul de armare al soluției — perechea lui `armare_3d.rezumat`.
 *  Câmpurile `t_*` sunt id-urile de TIP ale grupurilor desenate (bară / etrier),
 *  `clasa` clasa de execuție a elementului; lipsesc (null) când cantitățile
 *  nu s-au putut face — atunci se desenează la fel, doar fără evidențiere. */
export interface ArmaturaRezumat {
  stalpi: Record<string, {
    n: number; phi: number | null; nb: number; nh: number;
    e_phi: number | null; e_pas: number | null;
    /** petrecerea nivelului: cota de la fața de sus a plăcii și lungimea ei.
     *  `null` la nivelurile prin care bara trece întreagă. */
    p_z: number | null; p_l0: number | null;
    t_bare: string | null; t_etr: string | null; clasa: string | null;
  }>;
  grinzi: Record<string, {
    sus: number; phi_sus: number | null; jos: number; phi_jos: number | null;
    e_phi: number | null; e_pas: number | null;
    /** petrecerile barelor continue, pe abscisa măsurată de la nodul i */
    innadiri: { x: number; l0: number; sus: boolean }[];
    t_sus: string | null; t_jos: string | null; t_etr: string | null;
    clasa: string | null;
  }>;
  pereti: Record<string, {
    v_phi: number | null; v_pas: number | null;
    o_phi: number | null; o_pas: number | null;
    t_v: string | null; t_o: string | null;
  }>;
  placa: Record<string, {
    x_phi: number | null; x_pas: number | null;
    y_phi: number | null; y_pas: number | null;
    t_x: string | null; t_y: string | null;
  }>;
  /** pe NUMELE AXULUI («Ax 1»), nu pe tronson: grinda de fundare se armează
   *  pe ax, iar modelul o dă tăiată în bucăți de mesh */
  fundatie: Record<string, {
    sus: number; phi_sus: number | null; jos: number; phi_jos: number | null;
    e_phi: number | null; e_pas: number | null;
    t_phi: number | null; t_pas: number | null;
    rep: number; rep_phi: number | null;
    t_sus: string | null; t_jos: string | null; t_rep: string | null;
    t_talpa: string | null; t_etr: string | null; clasa: string | null;
  }>;
  /** listele din care scena își face meniul de evidențiere */
  tipuri: Record<FamilieComponenta, TipComponenta[]>;
  acoperire_m: number;
  /** betonul turnat în pământ are acoperirea LUI, de două ori mai mare */
  acoperire_fundatie_m: number;
  are: { stalpi: number; grinzi: number; pereti: number; placa: number;
         fundatie: number };
}

/** Fundația DE DESENAT, așa cum o calculează scena (`fundareaDesenata`):
 *  tronsoanele și secțiunea pe ax. Se primește de-a gata, ca regula «ce
 *  fundație se desenează» să rămână într-un singur loc. */
export interface FundatiaDesenata {
  grinzi: FemGrindaFundare[];
  fnd: FemFundatie;
}

/** Cât se îngroașă o bară față de raza ei reală, și sub ce rază nu coboară.
 *  Un compromis scris, nu unul întâmplat: la 1,6× un Ø20 rămâne vizibil
 *  distinct de un Ø12, iar pragul ține barele subțiri pe ecran. */
const EXAGERARE = 1.6;
const RAZA_MIN_M = 0.011;

/** Cât se ÎNGROAȘĂ o bară cât timp e evidențiată, și sub ce rază nu coboară
 *  nici atunci.
 *
 *  Culoarea singură nu ajunge. Un stâlp aprins e o suprafață de zeci de mii
 *  de pixeli: aurul se vede din cealaltă parte a clădirii. Un Ø10 desenat la
 *  raza lui e, la scara unei clădiri de 20 m, sub un pixel — aurit, rămâne
 *  sub un pixel, adică omul alege «tipul 3» și nu se schimbă nimic pe ecran.
 *  Ce se vede la o bară nu e culoarea, e GROSIMEA ei; deci ce se evidențiază
 *  crește, iar restul oțelului rămâne la raza lui și se retrage în culoare.
 *  Pragul jos ține pe ecran și etrierul de Ø6, cel mai subțire din carcasă. */
const INGROSARE_EVID = 2.6;
const RAZA_EVID_MIN_M = 0.034;

/** Pasul minim DESENAT, în metri, pentru fiecare familie de armătură
 *  repetitivă. */
const PAS_MIN = { etrier: 0.50, plasa: 0.90, perete: 0.70,
                  fundatie: 0.60, talpa: 0.75 };

/** Câte instanțe de cilindru are voie armătura întregii clădiri.
 *
 *  Nu e o limită de memorie, ci una de cadru: instanțele sunt un singur
 *  desen, dar triunghiurile lor se transformă la fiecare cadru, iar harta de
 *  umbră le mai parcurge o dată. Peste buget, familiile repetitive (etrieri,
 *  plase) se răresc — se pierde densitatea, care oricum nu se citește la
 *  scara clădirii, nu se pierde nimic din ce arată desenul. */
const BUGET_INSTANTE = 55_000;

/** Peste atâtea bare într-un ochi de placă, plasa lui nu se mai desenează:
 *  un ochi mare la pas mic e o hașură, nu o informație. */
const MAX_LINII_OCHI = 70;

const raza = (phi: number | null | undefined) =>
  Math.max((phi ?? 12) / 2000 * EXAGERARE, RAZA_MIN_M);

/** Ciocul de 135° cu care se închide etrierul (P100-1 §5.7.1(5)): 10Ø, dar
 *  nu sub cât se vede la scara clădirii. */
const cioc = (phi: number | null | undefined) =>
  Math.max(10 * (phi ?? 8) / 1000, 0.06);

/** Pasul desenat: cel real, rărit până trece de pragul familiei. */
function pasDesenat(realCm: number | null | undefined, minim: number) {
  const real = (realCm ?? 20) / 100;
  if (real <= 0) return minim;
  return real * Math.max(1, Math.ceil(minim / real));
}

/** Un cilindru de desenat, cu culoarea lui și TIPUL de componentă din care
 *  face parte (`cat` bare / etrieri, `tip` id-ul) — după ele se evidențiază. */
type Bara = {
  p0: THREE.Vector3; p1: THREE.Vector3; r: number; c: number;
  cat: "bare" | "etrieri" | null; tip: string | null;
};

/** Colectorul: adună cilindrii — bare longitudinale, laturi de etrier, bare
 *  de plasă — și numără fiecare familie pentru bugetul de instanțe. */
class Colector {
  bare: Bara[] = [];
  nrBare = 0;
  nrEtrieri = 0;
  nrPlasa = 0;

  private adauga(p0: THREE.Vector3, p1: THREE.Vector3, r: number, c: number,
                 cat: "bare" | "etrieri" | null, tip: string | null) {
    if (p0.distanceToSquared(p1) > 1e-6) {
      this.bare.push({ p0, p1, r, c, cat, tip });
    }
  }

  /** o bară longitudinală, cu tipul ei de componentă */
  bara(p0: THREE.Vector3, p1: THREE.Vector3, r: number,
       c: number = REALIST.otel, tip: string | null = null) {
    this.nrBare++;
    this.adauga(p0, p1, r, c, "bare", tip);
  }

  /** o bară de plasă (perete, placă, talpă de fundație) */
  plasa(p0: THREE.Vector3, p1: THREE.Vector3, r: number,
        tip: string | null = null) {
    this.nrPlasa++;
    this.adauga(p0, p1, r, REALIST.otelPlasa, "bare", tip);
  }

  /** Un ETRIER închis: laturile, prelungite cu o rază la fiecare capăt ca
   *  să se închidă colțurile, plus cele două ciocuri de 135° cu care se
   *  leagă capetele. Fără prelungire, la fiecare colț rămâne o crestătură
   *  cât grosimea barei — pe o clădire cu mii de etrieri, ea e tot ce se
   *  vede. */
  etrier(pct: THREE.Vector3[], r: number, lCioc: number,
         tip: string | null = null) {
    this.nrEtrieri++;
    const d = new THREE.Vector3();
    for (let i = 0; i < pct.length; i++) {
      const a = pct[i];
      const b = pct[(i + 1) % pct.length];
      d.subVectors(b, a);
      if (d.lengthSq() < 1e-9) continue;
      d.normalize();
      this.adauga(a.clone().addScaledVector(d, -r),
        b.clone().addScaledVector(d, r), r, REALIST.otelEtrier, "etrieri", tip);
    }
    // ciocurile: din colțul de închidere, spre INTERIORUL etrierului
    if (pct.length >= 3 && lCioc > 0) {
      const col = pct[0];
      const spre = new THREE.Vector3()
        .addVectors(pct[1], pct[pct.length - 1])
        .multiplyScalar(0.5).sub(col);
      if (spre.lengthSq() > 1e-9) {
        spre.normalize();
        for (const vecin of [pct[1], pct[pct.length - 1]]) {
          const start = col.clone().addScaledVector(
            new THREE.Vector3().subVectors(vecin, col).normalize(), 2 * r);
          this.adauga(start, start.clone().addScaledVector(spre, lCioc),
            r, REALIST.otelEtrier, "etrieri", tip);
        }
      }
    }
  }
}

/** Cât se retrage spre interior bara care VINE de dedesubt, ca petrecerea să
 *  se vadă ca două bare alăturate, nu ca una singură. Pe șantier bara chiar
 *  se îndoaie: petrecerea stă în interiorul carcasei de deasupra. */
const retras = (v: number, r: number) =>
  v - Math.sign(v) * Math.min(2.4 * r, Math.abs(v));

// --- stâlpul ---------------------------------------------------------------

/** Pozițiile barelor pe PERIMETRUL secțiunii, în axele locale ale stâlpului
 *  (x = de-a lungul lui `dir`, latura `h`; y = transversal, latura `b`).
 *  `nb`/`nh` sunt barele de pe fiecare latură, colțurile numărate o dată. */
function pozitiiStalp(nb: number, nh: number, ah: number, ab: number) {
  const nx = Math.max(2, nh || 2);
  const ny = Math.max(2, nb || 2);
  const p: [number, number][] = [];
  for (let i = 0; i < nx; i++) {
    const x = -ah + (2 * ah * i) / (nx - 1);
    p.push([x, -ab], [x, ab]);
  }
  for (let j = 1; j < ny - 1; j++) {
    const y = -ab + (2 * ab * j) / (ny - 1);
    p.push([-ah, y], [ah, y]);
  }
  return p;
}

/** Unde stă CARCASA stâlpului: centrul și semilaturile ei, în axele lui.
 *
 *  În betonul stâlpului, nu în nod. Un dreptunghi din Pasul 4 poate sta
 *  alunecat față de nodul lui — capacitatea e aceeași oriunde ar sta
 *  (`armare_stalpi._poligonul_sectiunii`) —, iar pe Olteniței P+4 ST20.1
 *  (90 × 30) are nodul la 47 cm de centrul amprentei: carcasa pusă în nod
 *  ieșea pe jumătate din beton.
 *
 *  La stâlpul în L/T nodul e intersecția axelor, iar piciorul dominant
 *  h × b PORNEȘTE din el — nu stă centrat pe el. Carcasa centrată în nod
 *  ieșea pe jumătate prin talpa T-ului: lamela ST8.14 (Demo, C4-T2), 6,3 m
 *  pe inimă, își desena carcasa cu 3,1 m în afara betonului, iar în
 *  «Realist» stâlpul arăta altă formă decât în «Clasic». Pe toate
 *  detaliile de pe stefan2, carcasa centrată în nod stătea în beton la 258
 *  din 5 136 de niveluri în L/T. Așa că inima se caută în amprentă: h de la
 *  capătul DEPĂRTAT de nod spre el, apoi de la celălalt capăt (un braț
 *  scurt al L-ului poate trece de capătul inimii), apoi toată întinderea pe
 *  `dir`. Prima așezare care stă toată în beton câștigă — pe aceleași
 *  date, toate cele 5 136. */
function carcasaStalp(s: FemStalp, j: { x_m: number; y_m: number },
                      acop: number) {
  const d = s.dir ?? [1, 0];
  const dl = Math.hypot(d[0], d[1]) || 1;
  const u = [d[0] / dl, d[1] / dl];
  let cx = j.x_m;
  let cy = j.y_m;
  let ah = s.h_m / 2 - acop;
  let ab = s.b_m / 2 - acop;
  const amp = s.amprenta_m;
  if (amp && amp.length >= 3) {
    let u0 = Infinity; let u1 = -Infinity; let v0 = Infinity; let v1 = -Infinity;
    for (const [x, y] of amp) {
      const a = (x - j.x_m) * u[0] + (y - j.y_m) * u[1];
      const b = -(x - j.x_m) * u[1] + (y - j.y_m) * u[0];
      u0 = Math.min(u0, a); u1 = Math.max(u1, a);
      v0 = Math.min(v0, b); v1 = Math.max(v1, b);
    }
    // dreptunghi = amprenta își umple cutia; un L/T lasă un gol între brațe
    if (arie(amp) > 0.95 * (u1 - u0) * (v1 - v0)) {
      const um = (u0 + u1) / 2;
      const vm = (v0 + v1) / 2;
      cx = j.x_m + um * u[0] - vm * u[1];
      cy = j.y_m + um * u[1] + vm * u[0];
      ah = (u1 - u0) / 2 - acop;
      ab = (v1 - v0) / 2 - acop;
    } else {
      // inima: pe axa nodului (v = 0), lungă cât h — sau cât amprenta, dacă
      // amprenta e mai scurtă
      const L = Math.min(s.h_m, u1 - u0);
      const [departe, aproape] = Math.abs(u1) >= Math.abs(u0)
        ? [u1 - L / 2, u0 + L / 2] : [u0 + L / 2, u1 - L / 2];
      const inBeton = (um: number, a: number) =>
        [-a, 0, a].every((du) => [-ab, ab].every((dv) => inPoligon(
          j.x_m + (um + du) * u[0] - dv * u[1],
          j.y_m + (um + du) * u[1] + dv * u[0], amp)));
      const asezari: [number, number][] = [
        [departe, L / 2 - acop], [aproape, L / 2 - acop],
        [(u0 + u1) / 2, (u1 - u0) / 2 - acop]];
      const [um, a] = asezari.find(([m, x]) => inBeton(m, x)) ?? asezari[0];
      cx = j.x_m + um * u[0];
      cy = j.y_m + um * u[1];
      ah = a;
    }
  }
  return { cx, cy, ah, ab, rot: Math.atan2(u[1], u[0]) };
}

function stalpi(c: Colector, r: FemResult, rez: ArmaturaRezumat,
                nd: Map<number, { x_m: number; y_m: number; z_m: number }>,
                rarire: number) {
  const acop = rez.acoperire_m;
  const tPl = r.model.grosime_placa_m ?? 0.15;
  // cine stă DEASUPRA și cine DEDESUBT: nodul de sus al unui stâlp e nodul de
  // jos al celui de deasupra. De aici se știe dacă bara mai are unde urca sau
  // se îndoaie în nod, și dacă sub nivel e placă sau fundație.
  const pesteNod = new Map<number, typeof r.model.stalpi[number]>();
  const subNod = new Set<number>();
  for (const s of r.model.stalpi ?? []) {
    pesteNod.set(s.nod_jos, s);
    subNod.add(s.nod_sus);
  }
  for (const s of r.model.stalpi ?? []) {
    const a = s.id_plan ? rez.stalpi[s.id_plan] : undefined;
    if (!a) continue;
    const j = nd.get(s.nod_jos);
    const k = nd.get(s.nod_sus);
    if (!j || !k) continue;
    const { cx, cy, ah, ab, rot } = carcasaStalp(s, j, acop);
    const cos = Math.cos(rot);
    const sin = Math.sin(rot);
    if (ah <= 0 || ab <= 0) continue;
    const glob = (dx: number, dy: number, z: number) =>
      new THREE.Vector3(cx + dx * cos - dy * sin,
        cy + dx * sin + dy * cos, z);
    const rb = raza(a.phi);
    // Nodul stă în planul MEDIAN al plăcii, deci fața de sus a plăcii — de la
    // care Pasul 15 măsoară petrecerea — e la jumătate de grosime peste el.
    // La bază, unde sub stâlp e fundația, nu e nicio placă de ocolit.
    const zPod = subNod.has(s.nod_jos) ? j.z_m + tPl / 2 : j.z_m;
    const deasupra = pesteNod.get(s.nod_sus);
    const pz = a.p_z ?? 0;
    const l0 = a.p_l0 ?? 0;
    // CAPETELE BUCĂȚII DE BARĂ, nu ale nivelului. Jos: de la petrecere, dacă
    // nivelul are una — dedesubtul ei e bara de mai jos, pe care o desenează
    // tot nivelul ăsta (v. mai departe). Sus: până în nodul de deasupra, de
    // unde o ia mai departe petrecerea nivelului următor; iar când deasupra
    // nu mai e nimic, bara se îndoaie în nod (EC2 §8.4.4) — una care s-ar
    // opri dreaptă în el n-ar avea de unde-și lua efortul.
    const zPodSus = k.z_m + tPl / 2;
    const zSus = deasupra ? k.z_m : zPodSus - acop;
    for (const [dx, dy] of pozitiiStalp(a.nb, a.nh, ah, ab)) {
      c.bara(glob(dx, dy, l0 ? zPod + pz : j.z_m), glob(dx, dy, zSus), rb,
        REALIST.otel, a.t_bare);
      if (!deasupra) {
        // ciocul din nodul de sus, spre interiorul secțiunii
        c.bara(glob(dx, dy, zSus),
          glob(retras(dx, 6 * rb), retras(dy, 6 * rb), zSus), rb,
          REALIST.otel, a.t_bare);
      }
      if (l0 > 0) {
        // BARA VENITĂ DE JOS, pe lungimea petrecerii: retrasă spre interior
        // și cu cârligul cu care trece din carcasa de dedesubt în asta
        const ix = retras(dx, rb);
        const iy = retras(dy, rb);
        c.bara(glob(dx, dy, j.z_m), glob(ix, iy, zPod + 0.08), rb,
          REALIST.otelInnadire, a.t_bare);
        c.bara(glob(ix, iy, zPod + 0.08), glob(ix, iy, zPod + pz + l0), rb,
          REALIST.otelInnadire, a.t_bare);
      }
    }
    // etrierii URCĂ PRIN NOD: zona de nod e tocmai cea în care stâlpul are
    // nevoie de confinare (P100-1 §5.3.4.2.3), deci desenul n-are voie s-o
    // lase goală între grindă și placă.
    const re = raza(a.e_phi);
    const lCioc = cioc(a.e_phi);
    const pas = pasDesenat(a.e_pas, PAS_MIN.etrier * rarire);
    const H = k.z_m - j.z_m;
    const n = Math.max(1, Math.round(H / pas));
    for (let i = 0; i <= n; i++) {
      const z = j.z_m + (H * i) / n;
      c.etrier([glob(-ah, -ab, z), glob(ah, -ab, z),
        glob(ah, ab, z), glob(-ah, ab, z)], re, lCioc, a.t_etr);
    }
  }
}

// --- grinda ----------------------------------------------------------------

/** Cât trece bara unei grinzi CONTINUE peste nod, în grinda care o continuă,
 *  când în nod nu e niciun reazem de străbătut (grinda e doar tăiată acolo). */
const TRECERE_M = 0.12;

/** Cât merge o bară dreaptă în reazem, cel mult: lungimea de ancorare, ~40Ø.
 *  Peste ea bara nu mai are ce ține — iar un reazem lung (lamela în lungul
 *  căreia intră grinda) ar trage-o altfel metri întregi prin beton. */
const ancoraj = (phi: number | null | undefined) =>
  Math.max(40 * (phi ?? 14) / 1000, 0.3);

/** Pasul cu care se caută fața de dincolo a reazemului. */
const PAS_REAZEM_M = 0.01;

/** Cât rămâne în beton o rază din (x, y) pe versorul (ux, uy): până unde
 *  iese din ultimul poligon prin care trece fără întrerupere. 0 când pornește
 *  din afara tuturor. */
function inBeton(x: number, y: number, ux: number, uy: number,
                 poli: Amprenta[], max: number) {
  let dus = 0;
  for (let s = PAS_REAZEM_M / 2; s <= max; s += PAS_REAZEM_M) {
    const px = x + ux * s;
    const py = y + uy * s;
    if (!poli.some((p) => inPoligon(px, py, p))) break;
    dus = s;
  }
  return dus;
}

/** Capetele DESENATE ale unei grinzi: axa ei reală (`axaGrinzii`). */
function capeteGrinda(g: FemGrinda,
                      nd: Map<number, { x_m: number; y_m: number }>) {
  const i0 = nd.get(g.nod_i);
  const i1 = nd.get(g.nod_j);
  if (!i0 || !i1) return null;
  const { a, b } = axaGrinzii(g, i0, i1);
  return { ax: a[0], ay: a[1], bx: b[0], by: b[1] };
}

function grinzi(c: Colector, r: FemResult, rez: ArmaturaRezumat,
                nd: Map<number, { x_m: number; y_m: number; z_m: number }>,
                rarire: number) {
  const acop = rez.acoperire_m;
  // câte grinzi ajung în fiecare nod: una singură înseamnă NOD DE MARGINE,
  // unde barele nu au în ce continua și se întorc în stâlp
  const laNod = new Map<number, number>();
  // BETONUL DIN NOD, în care intră barele: stâlpii care pornesc sau se
  // opresc în el și grinzile care ajung în el (cu versorul lor, ca să se
  // știe care o continuă pe cea curentă și care vine din lateral)
  const stalpiLaNod = new Map<number, Amprenta[]>();
  const grinziLaNod = new Map<number, {
    g: FemGrinda; amp: Amprenta; u: [number, number];
    /** capătul desenat pe care grinda îl are în nodul ăsta */
    x: number; y: number;
  }[]>();
  for (const s of r.model.stalpi ?? []) {
    const j = nd.get(s.nod_jos);
    if (!j) continue;
    const amp = amprentaStalp(s, j);
    for (const nod of [s.nod_jos, s.nod_sus]) {
      stalpiLaNod.set(nod, [...(stalpiLaNod.get(nod) ?? []), amp]);
    }
  }
  for (const g of r.model.grinzi ?? []) {
    const k = capeteGrinda(g, nd);
    const L = k ? Math.hypot(k.bx - k.ax, k.by - k.ay) : 0;
    for (const nod of [g.nod_i, g.nod_j]) {
      laNod.set(nod, (laNod.get(nod) ?? 0) + 1);
      if (!k || L < 1e-6) continue;
      const laI = nod === g.nod_i;
      grinziLaNod.set(nod, [...(grinziLaNod.get(nod) ?? []), {
        g, amp: dreptunghiSegment(k.ax, k.ay, k.bx, k.by, g.bw_m),
        u: [(k.bx - k.ax) / L, (k.by - k.ay) / L],
        x: laI ? k.ax : k.bx, y: laI ? k.ay : k.by,
      }]);
    }
  }
  // pereții, cu cotele lor turnate (aceleași ca în desenul panoului) și
  // versorul, ca să se știe care stă în LUNGUL grinzii: peste acela grinda
  // doar trece, nu-și are reazemul în el
  const pereti = (r.model.pereti_beton ?? []).map((p) => {
    const h = p.h_desen_m ?? p.h_web_m;
    const z0 = p.z_jos_m ?? (p.z_sus_m
      - (p.e_offset_m ?? p.hf_m / 2 + p.h_web_m / 2) - p.h_web_m / 2);
    const L = Math.hypot(p.b_xy[0] - p.a_xy[0], p.b_xy[1] - p.a_xy[1]) || 1;
    return { amp: amprentaPerete(p), z0, z1: z0 + h,
      u: [(p.b_xy[0] - p.a_xy[0]) / L, (p.b_xy[1] - p.a_xy[1]) / L] };
  });
  /** Reazemul grinzii `g` în capătul ei (x, y) din `nod`: tot betonul de
   *  acolo, fără ea și fără grinzile și pereții paraleli cu ea (prin cea care
   *  o continuă bara ar merge până la capătul ei), plus dacă o continuă
   *  vreuna.
   *
   *  CONTINUĂ = paralelă ȘI pornește chiar de aici. Nodul nu e un punct al
   *  grinzii: la o lamelă, un singur nod ține ambele ei capete, iar grinzile
   *  prinse de ele ajung în el prin offseturi de metri — pe Olteniței P+4,
   *  nodul lui ST13.2 poartă și GR32.2 (în x = 0), și GR53.2 (în x = 5,18). */
  const reazem = (g: FemGrinda, nod: number, x: number, y: number,
                  u: [number, number], z0: number, z1: number) => {
    const poli = [...(stalpiLaNod.get(nod) ?? [])];
    let continua = false;
    for (const o of grinziLaNod.get(nod) ?? []) {
      if (o.g === g) continue;
      if (Math.abs(o.u[0] * u[0] + o.u[1] * u[1]) <= 0.87) poli.push(o.amp);
      else if (Math.hypot(o.x - x, o.y - y) < Math.max(o.g.bw_m, g.bw_m)) {
        continua = true;
      }
    }
    for (const p of pereti) {
      if (p.z0 < z1 && p.z1 > z0
        && Math.abs(p.u[0] * u[0] + p.u[1] * u[1]) <= 0.87) poli.push(p.amp);
    }
    return { poli, continua };
  };
  for (const g of r.model.grinzi ?? []) {
    const a = g.id_plan ? rez.grinzi[g.id_plan] : undefined;
    if (!a) continue;
    const i0 = nd.get(g.nod_i);
    const k = capeteGrinda(g, nd);
    if (!i0 || !k) continue;
    const { ax, ay, bx, by } = k;
    const L = Math.hypot(bx - ax, by - ay);
    if (L < 0.25) continue;
    const rot = Math.atan2(by - ay, bx - ax);
    const cos = Math.cos(rot);
    const sin = Math.sin(rot);
    const cx = (ax + bx) / 2;
    const cy = (ay + by) / 2;
    // aceeași convenție ca desenul grinzii: inima atârnă de sub placă
    const hf = g.hf_m ?? 0.15;
    const hIn = g.h_web_m ?? Math.max(g.h_m - hf, 0.05);
    const zSus = i0.z_m - hf / 2;
    const zJos = zSus - hIn;
    const ab = g.bw_m / 2 - acop;
    if (ab <= 0) continue;
    const glob = (dx: number, dy: number, z: number) =>
      new THREE.Vector3(cx + dx * cos - dy * sin,
        cy + dx * sin + dy * cos, z);
    // BARA INTRĂ ÎN REAZEM, nu se oprește la fața lui: trece de capătul
    // desenat al grinzii până la fața de DINCOLO a betonului din nod — stâlpul,
    // grinda care vine din lateral, peretele —, minus acoperirea.
    //
    // Nu cât e offsetul de capăt. Offsetul duce capătul desenat de la nod pe
    // axa reală a grinzii și poate fi TRANSVERSAL pe ea: pe Olteniței P+4,
    // GR53.5 pleacă dintr-un nod aflat la 5,18 m în lateral (capătul lamelei
    // ST13.5), iar lungimea lui, pusă în lungul grinzii, îi scotea barele
    // 5,3 m în afara clădirii. Căutată în beton, bara nu poate ieși din el.
    //
    // Căutată PE FIECARE BARĂ, nu pe axă: grinda poate fi mai lată decât
    // reazemul sau alunecată față de el (GR32.2 are 30 cm, lamela ST13.2 în
    // care intră începe la 3,6 cm de axa ei), iar bara de pe margine ar trece
    // altfel de colțul lamelei, în aer.
    const u: [number, number] = [cos, sin];
    const ri = reazem(g, g.nod_i, ax, ay, u, zJos, zSus);
    const rj = reazem(g, g.nod_j, bx, by, u, zJos, zSus);
    const MAX = 1.5;
    /** cât trece bara de pe rândul `dy` de capătul desenat: negativ = se
     *  oprește la acoperire de fața grinzii, când dincolo de ea nu e beton */
    const intrare = (laI: boolean, dy: number, phi: number | null) => {
      const [x, y, sens, r] = laI ? [ax, ay, -1, ri] : [bx, by, 1, rj];
      const p = inBeton(x - sin * dy, y + cos * dy, sens * cos, sens * sin,
        r.poli, MAX);
      return Math.min(r.continua ? Math.max(p - acop, TRECERE_M) : p - acop,
        ancoraj(phi));
    };
    const capat = (nod: number) => (laNod.get(nod) ?? 0) <= 1;
    const sir = (n: number, z: number, phi: number | null, jos: boolean,
                 tip: string | null) => {
      if (n < 1) return;
      const rb = raza(phi);
      // la un nod de margine, bara se întoarce în stâlp: cea de sus în jos,
      // cea de jos în sus — ancorarea din care își ia efortul
      const cot = Math.min(15 * (phi ?? 14) / 1000, hIn - 2 * acop);
      for (let i = 0; i < n; i++) {
        const dy = n === 1 ? 0 : -ab + (2 * ab * i) / (n - 1);
        const x0 = -L / 2 - intrare(true, dy, phi);
        const x1 = L / 2 + intrare(false, dy, phi);
        c.bara(glob(x0, dy, z), glob(x1, dy, z), rb, REALIST.otel, tip);
        if (cot > 0) {
          if (capat(g.nod_i)) {
            c.bara(glob(x0, dy, z), glob(x0, dy, jos ? z + cot : z - cot), rb,
              REALIST.otel, tip);
          }
          if (capat(g.nod_j)) {
            c.bara(glob(x1, dy, z), glob(x1, dy, jos ? z + cot : z - cot), rb,
              REALIST.otel, tip);
          }
        }
      }
      // PETRECERILE: pe lungimea l₀ sunt două bare, iar a doua se vede
      // retrasă spre interiorul carcasei și în culoarea ei
      for (const p of a.innadiri ?? []) {
        if (p.sus === jos || !(p.l0 > 0)) continue;
        const x0 = Math.max(p.x - p.l0 / 2, 0) - L / 2;
        const x1 = Math.min(p.x + p.l0 / 2, L) - L / 2;
        if (x1 - x0 < 0.05) continue;
        // a doua bară stă lângă prima și puțin mai spre inima grinzii: o
        // singură bară pe rând n-ar avea vecin de care să se retragă, iar
        // petrecerea ar ieși desenată exact peste bara pe care o continuă
        const zi = jos ? z + 2.4 * rb : z - 2.4 * rb;
        for (let i = 0; i < n; i++) {
          const dy = n === 1 ? 0 : -ab + (2 * ab * i) / (n - 1);
          c.bara(glob(x0, retras(dy, rb), zi), glob(x1, retras(dy, rb), zi),
            rb, REALIST.otelInnadire, tip);
        }
      }
    };
    sir(a.sus, zSus - acop, a.phi_sus, false, a.t_sus);
    sir(a.jos, zJos + acop, a.phi_jos, true, a.t_jos);
    const re = raza(a.e_phi);
    const lCioc = cioc(a.e_phi);
    const pas = pasDesenat(a.e_pas, PAS_MIN.etrier * rarire);
    const n = Math.max(1, Math.round(L / pas));
    for (let i = 0; i <= n; i++) {
      const dx = -L / 2 + (L * i) / n;
      c.etrier([glob(dx, -ab, zJos + acop), glob(dx, ab, zJos + acop),
        glob(dx, ab, zSus - acop), glob(dx, -ab, zSus - acop)], re, lCioc,
      a.t_etr);
    }
  }
}

// --- pereții de beton ------------------------------------------------------

function pereti(c: Colector, r: FemResult, rez: ArmaturaRezumat,
                rarire: number) {
  for (const p of r.model.pereti_beton ?? []) {
    const a = p.id_plan ? rez.pereti[p.id_plan] : undefined;
    if (!a) continue;
    const [x0, y0] = p.a_xy;
    const [x1, y1] = p.b_xy;
    const L = Math.hypot(x1 - x0, y1 - y0);
    if (L < 0.2) continue;
    const h = p.h_desen_m ?? p.h_web_m;
    const zJos = p.z_jos_m ?? 0;
    if (!h || h <= 0) continue;
    const ux = (x1 - x0) / L;
    const uy = (y1 - y0) / L;
    // plasa se desenează pe planul MEDIAN al peretelui: două plase, una pe
    // fiecare față, ar fi două rețele suprapuse la 25 cm distanță — pe ecran,
    // moiré
    const rv = raza(a.v_phi);
    const ro = raza(a.o_phi);
    const pasV = pasDesenat(a.v_pas, PAS_MIN.perete * rarire);
    const nv = Math.max(1, Math.round(L / pasV));
    for (let i = 0; i <= nv; i++) {
      const t = (L * i) / nv;
      c.plasa(new THREE.Vector3(x0 + ux * t, y0 + uy * t, zJos + 0.03),
        new THREE.Vector3(x0 + ux * t, y0 + uy * t, zJos + h - 0.03), rv,
        a.t_v);
    }
    const pasO = pasDesenat(a.o_pas, PAS_MIN.perete * rarire);
    const no = Math.max(1, Math.round(h / pasO));
    for (let i = 0; i <= no; i++) {
      const z = zJos + (h * i) / no;
      c.plasa(new THREE.Vector3(x0, y0, z), new THREE.Vector3(x1, y1, z), ro,
        a.t_o);
    }
  }
}


// --- grinzile de fundare ---------------------------------------------------

/** Tronsoanele de mesh ale unui ax, unite în segmentele care se TOARNĂ.
 *
 *  Modelul dă grinda de fundare tăiată în bucăți de 20 cm — 279 pe clădirea
 *  de referință. Armate una câte una, ar ieși 279 de carcase scurte, adică o
 *  omidă în loc de o grindă. Se unesc pe ax, pe direcție și pe offsetul
 *  perpendicular (aceeași dreaptă), apoi pe intervale care se ating.
 */
function axeUnite(
  f: FundatiaDesenata,
  nd: Map<number, { x_m: number; y_m: number; z_m: number }>,
) {
  type Grup = { ax: string; u: [number, number]; perp: number;
                B: number; Df: number; int: [number, number][] };
  const grupuri = new Map<string, Grup>();
  for (const g of f.grinzi) {
    const a = nd.get(g.nod_i);
    const b = nd.get(g.nod_j);
    if (!a || !b) continue;
    const dx = b.x_m - a.x_m;
    const dy = b.y_m - a.y_m;
    const ln = Math.hypot(dx, dy);
    if (ln < 1e-9) continue;
    let ux = dx / ln;
    let uy = dy / ln;
    if (ux < -1e-9 || (Math.abs(ux) < 1e-9 && uy < 0)) { ux = -ux; uy = -uy; }
    const ax = g.ax ?? "";
    const peAx = f.fnd.pe_ax?.[ax] ?? {};
    const B = g.B_m ?? peAx.B_m ?? f.fnd.B_m ?? 0.6;
    const Df = g.Df_m ?? peAx.Df_m ?? f.fnd.Df_m ?? 1.0;
    const perp = Math.round((-uy * a.x_m + ux * a.y_m) * 100) / 100;
    const cheie = `${ax}|${ux.toFixed(3)}|${uy.toFixed(3)}|${perp}|${B}|${Df}`;
    const t0 = ux * a.x_m + uy * a.y_m;
    const t1 = ux * b.x_m + uy * b.y_m;
    const gr = grupuri.get(cheie)
      ?? { ax, u: [ux, uy] as [number, number], perp, B, Df, int: [] };
    gr.int.push([Math.min(t0, t1), Math.max(t0, t1)]);
    grupuri.set(cheie, gr);
  }
  const out: { ax: string; a: [number, number]; b: [number, number];
               B: number; Df: number }[] = [];
  for (const g of grupuri.values()) {
    g.int.sort((p, q) => p[0] - q[0]);
    let [c0, c1] = g.int[0];
    const runs: [number, number][] = [];
    for (const [t0, t1] of g.int.slice(1)) {
      if (t0 <= c1 + 0.02) c1 = Math.max(c1, t1);
      else { runs.push([c0, c1]); [c0, c1] = [t0, t1]; }
    }
    runs.push([c0, c1]);
    const [ux, uy] = g.u;
    for (const [t0, t1] of runs) {
      out.push({
        ax: g.ax, B: g.B, Df: g.Df,
        a: [ux * t0 - uy * g.perp, uy * t0 + ux * g.perp],
        b: [ux * t1 - uy * g.perp, uy * t1 + ux * g.perp],
      });
    }
  }
  return out;
}

function fundatie(c: Colector, rez: ArmaturaRezumat, f: FundatiaDesenata,
                  nd: Map<number, { x_m: number; y_m: number; z_m: number }>,
                  rarire: number) {
  const acop = rez.acoperire_fundatie_m ?? 0.05;
  const zSus = -(f.fnd.h_peste_m ?? 0.3);
  for (const run of axeUnite(f, nd)) {
    const a = rez.fundatie[run.ax];
    if (!a) continue;
    const peAx = f.fnd.pe_ax?.[run.ax] ?? {};
    const hT = peAx.h_talpa_m ?? f.fnd.h_talpa_m ?? 0.3;
    const bIn = peAx.b_inima_m ?? f.fnd.b_inima_m ?? 0.3;
    const zJos = -run.Df;
    const H = zSus - zJos;
    if (H <= 0.05) continue;
    const L = Math.hypot(run.b[0] - run.a[0], run.b[1] - run.a[1]);
    if (L < 0.4) continue;
    const rot = Math.atan2(run.b[1] - run.a[1], run.b[0] - run.a[0]);
    const cos = Math.cos(rot);
    const sin = Math.sin(rot);
    const cx = (run.a[0] + run.b[0]) / 2;
    const cy = (run.a[1] + run.b[1]) / 2;
    const glob = (dx: number, dy: number, z: number) =>
      new THREE.Vector3(cx + dx * cos - dy * sin,
        cy + dx * sin + dy * cos, z);
    // barele longitudinale ale INIMII: cele de jos stau în talpă, la fața ei
    const ai = bIn / 2 - acop;
    const sir = (n: number, z: number, phi: number | null, semi: number,
                 tip: string | null) => {
      if (n < 1 || semi <= 0) return;
      const rb = raza(phi);
      for (let i = 0; i < n; i++) {
        const dy = n === 1 ? 0 : -semi + (2 * semi * i) / (n - 1);
        c.bara(glob(-L / 2, dy, z), glob(L / 2, dy, z), rb, REALIST.otel, tip);
      }
    };
    sir(a.jos, zJos + acop, a.phi_jos, ai, a.t_jos);
    sir(a.sus, zSus - acop, a.phi_sus, ai, a.t_sus);
    // barele de repartiție ale TĂLPII: leagă plasa în lungul ei
    sir(a.rep, zJos + hT - acop, a.rep_phi, run.B / 2 - acop, a.t_rep);
    // etrierii: dreptunghi pe toată înălțimea secțiunii, la lățimea inimii
    if (ai > 0) {
      const re = raza(a.e_phi);
      const pasE = pasDesenat(a.e_pas, PAS_MIN.fundatie * rarire);
      const nE = Math.max(1, Math.round(L / pasE));
      for (let i = 0; i <= nE; i++) {
        const dx = -L / 2 + (L * i) / nE;
        c.etrier([glob(dx, -ai, zJos + acop), glob(dx, ai, zJos + acop),
          glob(dx, ai, zSus - acop), glob(dx, -ai, zSus - acop)],
        re, cioc(a.e_phi), a.t_etr);
      }
    }
    // plasa transversală a TĂLPII: ea ține consola la încovoiere, deci fără
    // ea talpa ar arăta ca beton simplu
    const at = run.B / 2 - acop;
    if (at > 0 && a.t_pas) {
      const rt = raza(a.t_phi);
      const pasT = pasDesenat(a.t_pas, PAS_MIN.talpa * rarire);
      const nT = Math.max(1, Math.round(L / pasT));
      for (let i = 0; i <= nT; i++) {
        const dx = -L / 2 + (L * i) / nT;
        c.plasa(glob(dx, -at, zJos + acop), glob(dx, at, zJos + acop), rt,
          a.t_talpa);
      }
    }
  }
}

// --- plasa plăcilor --------------------------------------------------------

/** Intervalele în care o dreaptă axată taie ochiul (conturul MINUS goluri):
 *  regula par–impar peste toate inelele. Cu ea, plasa se oprește la marginea
 *  ochiului și ocolește golul de scară — aceeași regulă ca în `Ochi3D`. */
function intervale(inele: [number, number][][], t: number, peX: boolean) {
  const taieturi: number[] = [];
  for (const inel of inele) {
    for (let i = 0; i < inel.length; i++) {
      const [x1, y1] = inel[i];
      const [x2, y2] = inel[(i + 1) % inel.length];
      const a = peX ? y1 : x1;
      const b = peX ? y2 : x2;
      if ((a <= t && t < b) || (b <= t && t < a)) {
        const f = (t - a) / (b - a);
        taieturi.push(peX ? x1 + f * (x2 - x1) : y1 + f * (y2 - y1));
      }
    }
  }
  taieturi.sort((p, q) => p - q);
  const out: [number, number][] = [];
  for (let i = 0; i + 1 < taieturi.length; i += 2) {
    if (taieturi[i + 1] - taieturi[i] > 0.3) {
      out.push([taieturi[i], taieturi[i + 1]]);
    }
  }
  return out;
}

function inelelePanoului(p: FemPanou): [number, number][][] {
  const g = [
    ...((p.inele ?? []).map((i) => i.contur)),
    ...((p.zone_gol ?? []).map((z) => z.contur)),
  ].filter((x): x is [number, number][] => (x?.length ?? 0) >= 3);
  return [p.contur as [number, number][], ...g];
}

function placa(c: Colector, r: FemResult, rez: ArmaturaRezumat,
               rarire: number) {
  const t = r.model.grosime_placa_m ?? 0.15;
  const zNivel = new Map<number, number>();
  for (const e of r.model.etaje) zNivel.set(e.nivel_diafragma, e.z_m);
  for (const p of r.model.panouri ?? []) {
    const a = p.id ? rez.placa[p.id] : undefined;
    if (!a || !p.contur || p.contur.length < 3) continue;
    const z0 = zNivel.get(p.nivel);
    if (z0 === undefined) continue;
    const rx = raza(a.x_phi);
    const ry = raza(a.y_phi);
    // PLASA E LA FAȚA DE JOS a plăcii, sub acoperire, iar cele două rânduri
    // stau unul PESTE altul, cum se leagă: barele pe direcția scurtă dedesubt,
    // celelalte rezemate pe ele. Nodul e în planul median, deci fața de jos e
    // la jumătate de grosime sub el.
    const z = z0 - t / 2 + rez.acoperire_m;
    const inele = inelelePanoului(p);
    const xs = p.contur.map((q) => q[0]);
    const ys = p.contur.map((q) => q[1]);
    const pasX = pasDesenat(a.x_pas, PAS_MIN.plasa * rarire);
    const pasY = pasDesenat(a.y_pas, PAS_MIN.plasa * rarire);
    const nx = (Math.max(...xs) - Math.min(...xs)) / pasY;
    const ny = (Math.max(...ys) - Math.min(...ys)) / pasX;
    if (nx + ny > MAX_LINII_OCHI) continue;
    for (let k = Math.ceil(Math.min(...ys) / pasX) * pasX;
      k <= Math.max(...ys); k += pasX) {
      for (const [u, v] of intervale(inele, k, true)) {
        c.plasa(new THREE.Vector3(u, k, z + rx),
          new THREE.Vector3(v, k, z + rx), rx, a.t_x);
      }
    }
    for (let k = Math.ceil(Math.min(...xs) / pasY) * pasY;
      k <= Math.max(...xs); k += pasY) {
      for (const [u, v] of intervale(inele, k, false)) {
        c.plasa(new THREE.Vector3(k, u, z + 2 * rx + ry),
          new THREE.Vector3(k, v, z + 2 * rx + ry), ry, a.t_y);
      }
    }
  }
}

// --- asamblarea ------------------------------------------------------------

export interface GrupArmatura {
  grup: THREE.Group;
  /** bare longitudinale (cu tot cu petreceri și ciocuri) */
  nrBare: number;
  /** etrieri închiși */
  nrEtrieri: number;
  /** bare de plasă (pereți, plăci, talpa fundației) */
  nrPlasa: number;
  /** cilindri desenați, adică prețul pe cadru */
  nrInstante: number;
  /** cât s-a rărit pasul desenat ca să încapă în buget (1 = deloc) */
  rarire: number;
  /** Colorează după evidențiere: instanțele TIPULUI cerut (sau ale întregii
   *  familii) în aurul selecției, restul oțelului stins; `null` readuce
   *  culorile proprii. Un singur buffer de culori rescris — niciun desen în
   *  plus, nicio geometrie refăcută. */
  evidentiaza: (ev: Evidentiere) => void;
  /** PE COTĂ (timelapse-ul): rămân desenate doar barele al căror mijloc trece
   *  testul `f(z)` — ale etajelor armate deja; `null` le readuce pe toate.
   *  Instanțele ascunse se strâng la scară zero, fără altă geometrie. */
  arataDupaCota: (f: ((z: number) => boolean) | null) => void;
  dispose: () => void;
}

function aduna(r: FemResult, rez: ArmaturaRezumat,
               fund: FundatiaDesenata | null | undefined, rarire: number) {
  const nd = new Map(r.model.noduri.map((n) => [n.id, n]));
  const c = new Colector();
  stalpi(c, r, rez, nd, rarire);
  grinzi(c, r, rez, nd, rarire);
  pereti(c, r, rez, rarire);
  placa(c, r, rez, rarire);
  if (fund?.grinzi?.length) fundatie(c, rez, fund, nd, rarire);
  return c;
}

/** Armătura întregii clădiri: UN desen pe cadru.
 *
 *  Cilindrul are 6 laturi, nu 16: la grosimea la care se vede o bară,
 *  diferența nu se distinge, iar triunghiurile scad de aproape trei ori.
 */
export function construiesteArmatura(
  r: FemResult, rez: ArmaturaRezumat, fund?: FundatiaDesenata | null,
): GrupArmatura {
  // BUGETUL: se adună o dată, iar dacă armătura desenată nu încape, se
  // rărește și se adună din nou. A doua trecere costă milisecunde de CPU pe
  // clădirile mari — exact cele care altfel ar plăti la FIECARE cadru.
  let rarire = 1;
  let c = aduna(r, rez, fund, rarire);
  while (c.bare.length > BUGET_INSTANTE && rarire < 8) {
    rarire *= Math.min(2, Math.max(1.4, c.bare.length / BUGET_INSTANTE));
    c = aduna(r, rez, fund, rarire);
  }

  const grup = new THREE.Group();
  grup.name = "armatura";
  const deAruncat: { dispose: () => void }[] = [];
  let inst: THREE.InstancedMesh | null = null;
  let aseaza: ((i: number, r: number) => void) | null = null;

  if (c.bare.length) {
    const geo = new THREE.CylinderGeometry(1, 1, 1, 6, 1, true);
    // culoarea vine PER INSTANȚĂ (bară, etrier, petrecere, plasă): materialul
    // rămâne unul singur, deci și desenul rămâne unul singur
    const mat = new THREE.MeshStandardMaterial({
      color: 0xffffff,
      metalness: 0.95,
      roughness: 0.42,
      envMapIntensity: 1.1,
    });
    inst = new THREE.InstancedMesh(geo, mat, c.bare.length);
    const m = new THREE.Matrix4();
    const q = new THREE.Quaternion();
    const sus = new THREE.Vector3(0, 1, 0);
    const dir = new THREE.Vector3();
    const mij = new THREE.Vector3();
    const sc = new THREE.Vector3();
    const col = new THREE.Color();
    const im = inst;
    // O instanță e cilindrul unitate întins pe bară: mijlocul, rotația spre
    // celălalt capăt, lungimea pe axă și raza pe celelalte două. `raza` e
    // singurul lucru care se mai schimbă după construcție (evidențierea).
    aseaza = (i: number, r: number) => {
      const b = c.bare[i];
      dir.subVectors(b.p1, b.p0);
      const len = dir.length();
      mij.addVectors(b.p0, b.p1).multiplyScalar(0.5);
      q.setFromUnitVectors(sus, dir.normalize());
      sc.set(r, len, r);
      im.setMatrixAt(i, m.compose(mij, q, sc));
    };
    c.bare.forEach((b, i) => {
      aseaza!(i, b.r);
      im.setColorAt(i, col.setHex(b.c));
    });
    inst.instanceMatrix.needsUpdate = true;
    if (inst.instanceColor) inst.instanceColor.needsUpdate = true;
    inst.castShadow = true;
    inst.frustumCulled = false;
    grup.add(inst);
    deAruncat.push(geo, mat);
  }

  // câte instanțe stau acum îngroșate: cât timp e zero și nu se cere nimic,
  // evidențierea nu are ce reașeza
  let ingrosate = 0;
  // filtrul pe cotă al timelapse-ului: barele ascunse au scara zero
  let filtruCota: ((z: number) => boolean) | null = null;
  const zeroM = new THREE.Matrix4().makeScale(0, 0, 0);
  const arataDupaCota = (f: ((z: number) => boolean) | null) => {
    if (!inst || !aseaza) return;
    filtruCota = f;
    c.bare.forEach((b, i) => {
      if (f && !f((b.p0.z + b.p1.z) / 2)) inst!.setMatrixAt(i, zeroM);
      else aseaza!(i, b.r);
    });
    inst.instanceMatrix.needsUpdate = true;
  };

  const evidentiaza = (ev: Evidentiere) => {
    if (!inst || !aseaza) return;
    const col = new THREE.Color();
    // familiile de BETON (stâlpi, grinzi, fundație) se evidențiază pe piesele
    // lor, nu pe oțel: armătura își păstrează culorile, ca să nu pară că a
    // fost aleasă și ea
    const peOtel = ev && (ev.cat === "bare" || ev.cat === "etrieri");
    const deReasezat = peOtel || ingrosate > 0;
    let n = 0;
    c.bare.forEach((b, i) => {
      let hex = b.c;
      let potrivit = false;
      if (peOtel) {
        potrivit = b.cat === ev.cat && (ev.tip == null || b.tip === ev.tip);
        hex = potrivit ? REALIST.selectat : REALIST.otelStins;
      }
      inst!.setColorAt(i, col.setHex(hex));
      if (potrivit) n++;
      if (deReasezat) {
        if (filtruCota && !filtruCota((b.p0.z + b.p1.z) / 2)) inst!.setMatrixAt(i, zeroM);
        else aseaza!(i, potrivit
          ? Math.max(b.r * INGROSARE_EVID, RAZA_EVID_MIN_M)
          : b.r);
      }
    });
    ingrosate = n;
    if (inst.instanceColor) inst.instanceColor.needsUpdate = true;
    if (deReasezat) inst.instanceMatrix.needsUpdate = true;
  };

  return {
    grup,
    nrBare: c.nrBare,
    nrEtrieri: c.nrEtrieri,
    nrPlasa: c.nrPlasa,
    nrInstante: c.bare.length,
    rarire,
    evidentiaza,
    arataDupaCota,
    dispose: () => deAruncat.forEach((o) => o.dispose()),
  };
}
